/**
 * Template Context Analyzer
 *
 * This service parses mustache-style template content to determine the current
 * context at a given cursor position. This enables context-aware merge variable
 * selection - when you're inside a {{#orderDetails}}...{{/orderDetails}} block,
 * the available variables are the lineItem properties.
 *
 * Supported syntax:
 * - {{variableName}} - Variable output
 * - {{#blockName}}...{{/blockName}} - Section/loop block (arrays and objects)
 * - {{^blockName}}...{{/blockName}} - Inverted section
 */

import {
	VariableFieldType,
	VariableTypeKey,
	getVariableTypeDefinition,
	type VariableTypeProperty,
} from '@supply-mover-2-0/shared';

// =============================================================================
// Types
// =============================================================================

/**
 * A block in the template context path.
 * Represents being inside a {{#blockName}}...{{/blockName}} section.
 */
export interface TemplateContextBlock {
	/** The variable name that opened this block */
	name: string;
	/** What type of block this is */
	blockType: 'array' | 'object' | 'conditional';
	/** For array blocks: what type are the items? */
	arrayType?: VariableTypeKey;
	/** For object blocks: what type is it? */
	objectType?: VariableTypeKey;
	/** Position where the block opens */
	startPosition: number;
	/** Position where the block closes (if found) */
	endPosition?: number;
}

/**
 * The result of analyzing template context at a cursor position.
 */
export interface TemplateContext {
	/** Stack of blocks we're currently inside (outermost to innermost) */
	path: Array<TemplateContextBlock>;
	/** The type key at the current innermost level (if any) */
	currentType?: VariableTypeKey;
	/** Whether the cursor is at root level (not inside any block) */
	isRoot: boolean;
}

/**
 * A variable that's available for insertion at the current context.
 */
export interface AvailableVariable {
	/** The variable key to insert */
	key: string;
	/** Human-readable label */
	label: string;
	/** The field type */
	type: VariableFieldType;
	/** Is this an array that can be iterated? */
	isArray: boolean;
	/** Is this an object that can be entered? */
	isObject: boolean;
	/** For arrays: what type are the items? */
	arrayType?: VariableTypeKey;
	/** For objects: what type is it? */
	objectType?: VariableTypeKey;
	/** Optional description */
	description?: string;
}

/**
 * A token in the parsed template.
 */
interface TemplateToken {
	type: 'text' | 'variable' | 'openBlock' | 'closeBlock' | 'invertedBlock';
	content: string;
	name?: string;
	position: number;
	length: number;
}

// =============================================================================
// Tokenizer
// =============================================================================

/**
 * Tokenize a template string into tokens.
 * Recognizes: {{variable}}, {{#block}}, {{/block}}, {{^block}}
 */
function tokenizeTemplate(content: string): Array<TemplateToken> {
	const tokens: Array<TemplateToken> = [];
	const regex = /\{\{([#^/]?)(\s*[\w.]+\s*)\}\}/g;
	let lastIndex = 0;
	let match: RegExpExecArray | null;

	while ((match = regex.exec(content)) !== null) {
		// Add text before this match
		if (match.index > lastIndex) {
			tokens.push({
				type: 'text',
				content: content.substring(lastIndex, match.index),
				position: lastIndex,
				length: match.index - lastIndex,
			});
		}

		const prefix = match[1] ?? '';
		const rawName = match[2];
		const name = rawName != null ? rawName.trim() : '';
		const fullMatch = match[0];

		if (prefix === '#') {
			tokens.push({
				type: 'openBlock',
				content: fullMatch,
				name,
				position: match.index,
				length: fullMatch.length,
			});
		} else if (prefix === '/') {
			tokens.push({
				type: 'closeBlock',
				content: fullMatch,
				name,
				position: match.index,
				length: fullMatch.length,
			});
		} else if (prefix === '^') {
			tokens.push({
				type: 'invertedBlock',
				content: fullMatch,
				name,
				position: match.index,
				length: fullMatch.length,
			});
		} else {
			tokens.push({
				type: 'variable',
				content: fullMatch,
				name,
				position: match.index,
				length: fullMatch.length,
			});
		}

		lastIndex = regex.lastIndex;
	}

	// Add remaining text
	if (lastIndex < content.length) {
		tokens.push({
			type: 'text',
			content: content.substring(lastIndex),
			position: lastIndex,
			length: content.length - lastIndex,
		});
	}

	return tokens;
}

// =============================================================================
// Context Analyzer
// =============================================================================

/**
 * Analyze the template context at a given cursor position.
 *
 * @param content The full template content
 * @param cursorPosition The cursor position (0-indexed)
 * @param rootVariables The available variables at the root level
 * @returns The context at the cursor position
 */
export function analyzeTemplateContext(
	content: string,
	cursorPosition: number,
	rootVariables: Array<AvailableVariable>,
): TemplateContext {
	const tokens = tokenizeTemplate(content);
	const blockStack: Array<TemplateContextBlock> = [];

	// Build a map of root variables for quick lookup
	const rootVarMap = new Map<string, AvailableVariable>();

	for (const v of rootVariables) {
		rootVarMap.set(v.key, v);
	}

	// Process tokens up to cursor position
	for (const token of tokens) {
		// Stop if we've passed the cursor
		if (token.position >= cursorPosition) {
			break;
		}

		if (token.type === 'openBlock' && token.name != null) {
			// Look up the variable to determine its type
			const variableName = token.name;
			let arrayType: VariableTypeKey | undefined;
			let objectType: VariableTypeKey | undefined;
			let blockType: 'array' | 'object' | 'conditional' = 'conditional';

			// Check in current context or root
			if (blockStack.length > 0) {
				// We're inside a block, look up the property in that context
				const currentBlock = blockStack[blockStack.length - 1];

				if (currentBlock != null) {
					const currentTypeKey =
						currentBlock.arrayType ?? currentBlock.objectType;

					if (currentTypeKey != null) {
						const typeDef = getVariableTypeDefinition(currentTypeKey);

						if (typeDef != null) {
							const prop = typeDef.properties[variableName];

							if (prop != null) {
								if (prop.type === VariableFieldType.ARRAY) {
									blockType = 'array';
									arrayType = prop.arrayType;
								} else if (prop.type === VariableFieldType.OBJECT) {
									blockType = 'object';
									objectType = prop.objectType;
								}
							}
						}
					}
				}
			} else {
				// At root level, check rootVariables
				const rootVar = rootVarMap.get(variableName);

				if (rootVar != null) {
					if (rootVar.isArray) {
						blockType = 'array';
						arrayType = rootVar.arrayType;
					} else if (rootVar.isObject) {
						blockType = 'object';
						objectType = rootVar.objectType;
					}
				}
			}

			blockStack.push({
				name: variableName,
				blockType,
				arrayType,
				objectType,
				startPosition: token.position,
			});
		} else if (token.type === 'closeBlock' && token.name != null) {
			// Pop matching block from stack
			const closeName = token.name;

			for (let i = blockStack.length - 1; i >= 0; i--) {
				const block = blockStack[i];

				if (block != null && block.name === closeName) {
					block.endPosition = token.position + token.length;
					blockStack.splice(i, 1);

					break;
				}
			}
		} else if (token.type === 'invertedBlock' && token.name != null) {
			// Inverted blocks are conditional (no type context change)
			blockStack.push({
				name: token.name,
				blockType: 'conditional',
				startPosition: token.position,
			});
		}
	}

	// Determine current type from innermost block
	let currentType: VariableTypeKey | undefined;

	if (blockStack.length > 0) {
		const innermost = blockStack[blockStack.length - 1];

		if (innermost != null) {
			currentType = innermost.arrayType ?? innermost.objectType;
		}
	}

	return {
		path: blockStack,
		currentType,
		isRoot: blockStack.length === 0,
	};
}

// =============================================================================
// Variable Resolution
// =============================================================================

/**
 * Get the available variables for a given template context.
 *
 * @param rootVariables Variables available at root level
 * @param context The current template context
 * @returns Variables available at the current context
 */
export function getAvailableVariablesForContext(
	rootVariables: Array<AvailableVariable>,
	context: TemplateContext,
): Array<AvailableVariable> {
	if (context.isRoot) {
		return rootVariables;
	}

	// We're inside a block, get properties from the type
	if (context.currentType == null) {
		// No type info for this block, return empty
		return [];
	}

	const typeDef = getVariableTypeDefinition(context.currentType);

	if (typeDef == null) {
		return [];
	}

	const entries = Object.entries(typeDef.properties) as Array<
		[string, VariableTypeProperty]
	>;

	return entries.map(
		([key, prop]): AvailableVariable => ({
			key,
			label: prop.label,
			type: prop.type,
			isArray: prop.type === VariableFieldType.ARRAY,
			isObject: prop.type === VariableFieldType.OBJECT,
			arrayType: prop.arrayType,
			objectType: prop.objectType,
			description: prop.description,
		}),
	);
}

/**
 * Convert a schema field to an AvailableVariable.
 * Useful for converting output schemas to the format needed by the analyzer.
 */
export function schemaFieldToAvailableVariable(
	key: string,
	field: {
		type: VariableFieldType;
		label: string;
		description?: string;
		arrayType?: VariableTypeKey;
		objectType?: VariableTypeKey;
	},
): AvailableVariable {
	return {
		key,
		label: field.label,
		type: field.type,
		isArray: field.type === VariableFieldType.ARRAY,
		isObject: field.type === VariableFieldType.OBJECT,
		arrayType: field.arrayType,
		objectType: field.objectType,
		description: field.description,
	};
}

/**
 * Generate the mustache syntax for inserting a variable.
 *
 * - Primitive variables: {{variableName}}
 * - Array variables: {{#variableName}}\n\n{{/variableName}}
 * - Object variables: {{#variableName}}\n\n{{/variableName}}
 *
 * @param variable The variable to insert
 * @param includeBlockSyntax If true, wrap arrays/objects in block syntax
 * @returns The mustache syntax to insert
 */
export function generateMustacheSyntax(
	variable: AvailableVariable,
	includeBlockSyntax: boolean = true,
): string {
	if (!includeBlockSyntax || (!variable.isArray && !variable.isObject)) {
		return `{{${variable.key}}}`;
	}

	// For arrays and objects, generate block syntax
	return `{{#${variable.key}}}\n\n{{/${variable.key}}}`;
}

/**
 * Get the cursor position offset for inserting content inside a block.
 * Returns the position after the opening tag + newline.
 */
export function getBlockInsertionOffset(variable: AvailableVariable): number {
	if (!variable.isArray && !variable.isObject) {
		return `{{${variable.key}}}`.length;
	}

	// Position cursor after opening tag + newline
	return `{{#${variable.key}}}\n`.length;
}
